import {propEq} from 'ramda';
import React from 'react';
import PropTypes from 'prop-types';
import {fetchJsonV1} from '@sinclair-digital/v2-content-service/lib/fetch';
import {default as Search} from '@sinclair-digital/sbg-storyline-components/src/components/Search';
import {default as MediaCard} from '@sinclair-digital/sbg-storyline-components/src/components/MediaCard';
// These imports from PackageSearch should probably be their own module - Steve Minor 5/23/19
import {searchResultsGeneratorV1} from '@sinclair-digital/sbg-storyline-components/src/components/PackageSearch/ajax';
import {
    enableFilters,
    disableFilters,
    mergeFilter,
    getBroadcastFilter,
} from '@sinclair-digital/sbg-storyline-components/src/components/PackageSearch/selectors';

const MEDIA_SEARCH_URL = '/platform/search/media';
const MEDIA_SUMMARY_URL = '/platform/media/summary';

export const defaultSearchOptions = {
    query: {
        searchText: {
            groupName: 'Search',
            filterType: 'searchbar',
            filters: [],
            placeholderText: 'search media',
            text: '',
        },
        fromDate: null,
        thruDate: null,
        scope: {
            groupName: 'Properties',
            filterType: 'radio',
            filters: [
                {
                    name: 'all',
                    text: 'all properties',
                    value: ['ALL'],
                },
                {
                    name: 'my',
                    text: 'my properties',
                    value: [], // populated at runtime
                    selected: true,
                },
            ],
        },
        packageType: {
            groupName: 'Type',
            filterType: 'checkbox',
            filters: [
                {
                    name: 'all',
                    value: ['IMAGE', 'AUDIO', 'VIDEO'],
                    text: 'all types',
                    selected: true,
                    exclusive: true,
                    basicFilter: true,
                },
                {
                    name: 'image',
                    value: ['IMAGE'],
                    text: 'images',
                    basicFilter: true,
                },
                {
                    name: 'audio',
                    value: ['AUDIO'],
                    text: 'audio',
                },
                {
                    name: 'video',
                    value: ['VIDEO'],
                    text: 'video',
                    basicFilter: true,
                },
                {
                    name: 'broadcast',
                    value: ['BROADCAST'],
                    text: 'broadcast',
                    exclusive: true,
                    basicFilter: true,
                    channel: null,
                    channels: [], // populated at runtime
                },
            ],
        },
        field: {
            groupName: 'Fields',
            filterType: 'checkbox',
            filters: [
                {
                    name: 'all',
                    value: [],
                    text: 'all fields',
                    selected: true,
                    exclusive: true,
                },
                {
                    name: 'title',
                    value: ['title'],
                    text: 'title',
                },
                {
                    name: 'caption',
                    value: ['caption'],
                    text: 'caption',
                },
                {
                    name: 'altText',
                    value: ['alt_text'],
                    text: 'alt text',
                },
                {
                    name: 'tags',
                    value: ['all_tags'],
                    text: 'tags',
                },
            ],
        },
        sortOptions: {
            groupName: 'Sort',
            filterType: 'radio',
            filters: [
                {
                    sortColumn: 'Date',
                    text: 'upload date',
                    sortDirection: 'descending',
                    selected: true,
                },
            ],
        },
    },
    showAdvancedFilters: true,
};

// mutates searchOptions
const populateMyPropertiesFilter = (searchOptions, myTargets) => {
    const myPropertiesValue = myTargets.map(t => t.site.name);
    const myPropertiesFilter = searchOptions.query.scope.filters.find(propEq('name', 'my'));
    myPropertiesFilter.value = myPropertiesValue;
};

// mutates searchOptions
const populateBroadcastFilter = (searchOptions, myTargets) => {
    const broadcastFilter = searchOptions.query.packageType.filters.find(propEq('name', 'broadcast'));
    broadcastFilter.channels = myTargets.map(target => ({
        name: `${target.site.callLetters} - Broadcasts`,
        value: target.site.callLetters,
    }));
    broadcastFilter.channel = broadcastFilter.channels[0].value;
};

export const beforeSearch = (prevQuery, nextQuery) => {
    const scopeFilters = nextQuery.scope.filters;
    const packageFilters = nextQuery.packageType.filters;
    const fieldFilters = nextQuery.field.filters;
    const matchName = propEq('name');
    const allFilter = packageFilters.find(matchName('all'));
    const broadcastFilter = packageFilters.find(matchName('broadcast'));

    let newScopeFilters = enableFilters(scopeFilters);
    let newFieldFilters = enableFilters(fieldFilters);

    if (broadcastFilter.selected) {
        newScopeFilters = disableFilters(scopeFilters);
        newFieldFilters = disableFilters(fieldFilters);
    } else {
        const tagFilter = newFieldFilters.find(matchName('tags'));
        tagFilter.disabled = allFilter && !allFilter.selected;
    }

    const query2 = mergeFilter('scope', newScopeFilters, nextQuery);
    return mergeFilter('field', newFieldFilters, query2);
};

function* broadcastResultsGenerator({callLetters}) {
    yield fetchJsonV1(`/api/video/assetsByChannel/${callLetters}`).then(response => response.data[0].assets);
}

function* mediaSearchResultsGenerator(query) {
    const broadcastFilter = getBroadcastFilter(query);
    const isBroadcastModeEnabled = broadcastFilter && broadcastFilter.selected;

    if (isBroadcastModeEnabled) {
        yield* broadcastResultsGenerator({callLetters: broadcastFilter.channel});
    } else {
        yield* searchResultsGeneratorV1({query, searchUrl: MEDIA_SEARCH_URL, summaryUrl: MEDIA_SUMMARY_URL});
    }
}

const mapResultToCard = (result, props) => <MediaCard {...props} data={result} />;

const MediaSearch = ({myTargets, enableUrlQueryPersistence}) => {
    populateMyPropertiesFilter(defaultSearchOptions, myTargets);
    populateBroadcastFilter(defaultSearchOptions, myTargets);
    return (
        <Search
            searchOptions={defaultSearchOptions}
            searchResultsGenerator={mediaSearchResultsGenerator}
            beforeSearch={beforeSearch}
            mapResultToCard={mapResultToCard}
            showSearchBar={true}
            selectionMode="multiple"
            enableUrlQueryPersistence={enableUrlQueryPersistence}
            isSearchResultStyleToggleEnabled={true}
        />
    );
};

MediaSearch.propTypes = {
    myTargets: PropTypes.array.isRequired,
    enableUrlQueryPersistence: PropTypes.bool,
};

MediaSearch.defaultProps = {
    enableUrlQueryPersistence: true,
};

export default MediaSearch;
